import { ChartViewMenu } from './ChartViewMenu'
import { chartBuckets } from './gross-sales-series'
import type { ChartMode } from './gross-sales-series'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import Decimal from 'decimal.js'
import { ChevronLeft, ChevronRight, Maximize2, TrendingDown, TrendingUp } from 'lucide-react'
import { Button, Card, FitMoneyValue, MoneyValue } from '@shared/design-system/ui'
import { cx } from '@shared/lib/cx'
import { vars } from '@shared/design-system/tokens/contract.css'
import { formatMoney } from '@shared/lib/format/currency'
import type { DashboardCurrencyOverview } from '../api/dashboard.contract'
import * as styles from './gross-sales-chart.css'

type Point = DashboardCurrencyOverview['grossSalesSeries'][number]

function dayLabel(day: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${day}T12:00:00Z`))
}

export function GrossSalesChart({
  series,
  currency,
  periodLabel,
  mode: controlledMode,
  onModeChange,
  onExpand,
  variant = 'card',
  highlight,
}: {
  series: Point[]
  currency: string
  periodLabel: string
  mode?: ChartMode
  onModeChange?: (mode: ChartMode) => void
  onExpand?: () => void
  variant?: 'card' | 'live'
  highlight?: ReactNode
}) {
  const id = useId().replaceAll(':', '')
  const plotRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const [width, setWidth] = useState(640)
  const [plotHeight, setPlotHeight] = useState(0)
  const [selected, setSelected] = useState<number | null>(null)
  const [ownMode, setOwnMode] = useState<ChartMode>('cumulative')
  const mode = controlledMode ?? ownMode
  const live = variant === 'live'
  const data = useMemo(() => chartBuckets(series, mode), [series, mode])
  const chartLabel = mode === 'cumulative' ? 'Bruto acumulado no período' : 'Bruto vendido por dia'
  const amount = series.reduce((sum, point) => sum.plus(point.amount.amount), new Decimal(0))
  const previous = series.reduce(
    (sum, point) => sum.plus(point.previousAmount.amount),
    new Decimal(0),
  )
  const delta = previous.isZero() ? null : amount.minus(previous).div(previous).times(100)
  const index = Math.min(selected ?? Math.max(0, data.length - 1), Math.max(0, data.length - 1))
  const active = data[index]
  const height = live ? Math.max(240, Math.round(plotHeight)) : width < 480 ? 240 : 280
  const compact = width < 480
  const left = compact ? 8 : 72
  const right = width - 16
  const bottom = height - 38
  const top = 24
  const maximum = Decimal.max(1, ...data.map((point) => point.amount))
    .times(1.15)
    .toNumber()
  const positions = data.map((point, i) => ({
    x:
      mode === 'daily'
        ? left + ((i + 0.5) * (right - left)) / data.length
        : data.length === 1
          ? (left + right) / 2
          : left + (i * (right - left)) / (data.length - 1),
    y: bottom - (point.amount.toNumber() / maximum) * (bottom - top),
  }))
  let line = positions.length ? `M ${positions[0]!.x} ${positions[0]!.y}` : ''
  for (let i = 1; i < positions.length; i++) {
    const a = positions[i - 1]!,
      b = positions[i]!,
      middle = (a.x + b.x) / 2
    line += ` C ${middle} ${a.y} ${middle} ${b.y} ${b.x} ${b.y}`
  }
  const tickCount = width < 270 ? 2 : width < 480 ? 3 : 6
  const ticks = Array.from(
    new Set(
      Array.from({ length: tickCount }, (_, i) =>
        Math.round((i * (data.length - 1)) / (tickCount - 1)),
      ),
    ),
  )
  const hasSales = !amount.isZero()
  useEffect(() => {
    const element = plotRef.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect
      if (rect?.width) setWidth(rect.width)
      if (rect?.height) setPlotHeight(rect.height)
    })
    // SVG observations report graphic bounds, not the CSS viewport. Measure the HTML wrapper.
    observer.observe(element)
    return () => observer.disconnect()
  }, [data.length, hasSales])

  function selectAt(clientX: number) {
    const x = clientX - (svgRef.current?.getBoundingClientRect().left ?? 0)
    setSelected(
      Math.max(
        0,
        Math.min(
          data.length - 1,
          mode === 'daily'
            ? Math.floor(((x - left) / (right - left)) * data.length)
            : Math.round(((x - left) / (right - left)) * (data.length - 1)),
        ),
      ),
    )
  }

  const content = (
    <>
      <div className={styles.actions}>
        {onExpand ? (
          <Button
            variant="text"
            aria-label="Abrir bruto vendido em tela cheia"
            data-testid="dashboard-chart-expand-btn"
            title="Tela cheia"
            className={styles.menuTrigger}
            onClick={onExpand}
          >
            <Maximize2 size={18} aria-hidden />
          </Button>
        ) : null}
        <ChartViewMenu
          mode={mode}
          onChange={(next) => {
            setOwnMode(next)
            onModeChange?.(next)
            setSelected(null)
          }}
        />
      </div>
      <div className={styles.header}>
        <div className={cx(styles.copy, live && styles.liveCopy)}>
          <h2 className={cx(styles.title, live && styles.liveTitle)}>Bruto vendido no período</h2>
          {live ? (
            <FitMoneyValue
              amount={amount.toFixed(3)}
              currency={currency}
              maxSize={128}
              minSize={40}
              fractionSize="display"
              className={styles.liveTotal}
            />
          ) : (
            <MoneyValue
              amount={amount.toFixed(3)}
              currency={currency}
              size="display"
              className={styles.total}
            />
          )}
          <span className={styles.caption}>
            {periodLabel} · {currency}
          </span>
        </div>
        <div className={styles.comparison}>
          {delta === null ? (
            <span className={styles.caption}>Sem base no período anterior</span>
          ) : (
            <span className={delta.isNegative() ? styles.negative : styles.positive}>
              {delta.isNegative() ? (
                <TrendingDown size={16} aria-hidden />
              ) : (
                <TrendingUp size={16} aria-hidden />
              )}{' '}
              {delta.isPositive() ? '+' : ''}
              {delta.toFixed(1).replace('.', ',')}%
            </span>
          )}
          {delta !== null ? (
            <span className={styles.caption}>vs. período anterior equivalente</span>
          ) : null}
          {highlight}
        </div>
      </div>
      {amount.isZero() ? (
        <div className={cx(styles.empty, live && styles.livePlotArea)}>
          Nenhuma venda aprovada em {currency} neste período.
        </div>
      ) : (
        <div ref={plotRef} className={live ? styles.livePlotArea : undefined}>
          <svg
            ref={svgRef}
            className={cx(styles.plot, live && styles.livePlot)}
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label={`${chartLabel} em ${currency}`}
            onPointerMove={(event) => {
              if (event.pointerType === 'mouse') selectAt(event.clientX)
            }}
            onClick={(event) => selectAt(event.clientX)}
          >
            <title>{chartLabel}</title>
            <desc>
              Pagamentos confirmados antes das taxas e perdas. Use os botões anteriores e próximos
              para consultar cada valor.
            </desc>
            <defs>
              <linearGradient id={`${id}-area`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={vars.color.accent} stopOpacity={0.2} />
                <stop offset="100%" stopColor={vars.color.accent} stopOpacity={0.01} />
              </linearGradient>
            </defs>
            {!compact ? (
              <text x={0} y={12} className={styles.axis}>
                {currency}
              </text>
            ) : null}
            {[0, 1, 2, 3].map((i) => (
              <g key={i}>
                <line
                  x1={left}
                  x2={right}
                  y1={bottom - (i * (bottom - top)) / 3}
                  y2={bottom - (i * (bottom - top)) / 3}
                  stroke={vars.color.border}
                  strokeWidth={0.7}
                />
                {!compact ? (
                  <text
                    x={left - 10}
                    y={bottom - (i * (bottom - top)) / 3 + 4}
                    textAnchor="end"
                    className={styles.axis}
                  >
                    {new Intl.NumberFormat('pt-BR', {
                      notation: 'compact',
                      maximumFractionDigits: 1,
                    }).format((maximum * i) / 3)}
                  </text>
                ) : null}
              </g>
            ))}
            {mode === 'cumulative' ? (
              <>
                <path
                  d={`${line} L ${positions[positions.length - 1]?.x} ${bottom} L ${positions[0]?.x} ${bottom} Z`}
                  fill={`url(#${id}-area)`}
                />
                <path
                  d={line}
                  fill="none"
                  stroke={vars.color.accent}
                  strokeWidth={2.5}
                  strokeLinecap="round"
                />
              </>
            ) : (
              positions.map((point, i) => {
                const barWidth = ((right - left) / data.length) * 0.68
                return (
                  <rect
                    key={data[i]!.label}
                    x={point.x - barWidth / 2}
                    y={point.y}
                    width={barWidth}
                    height={bottom - point.y}
                    rx={Math.min(7, barWidth / 2, (bottom - point.y) / 2)}
                    fill={vars.color.accent}
                    opacity={i === index ? 1 : 0.42}
                    data-daily-bar="true"
                  />
                )
              })
            )}
            {ticks.map(
              (i, tick) =>
                data[i] && (
                  <text
                    key={i}
                    x={positions[i]!.x}
                    y={height - 10}
                    textAnchor={tick === 0 ? 'start' : tick === ticks.length - 1 ? 'end' : 'middle'}
                    className={styles.axis}
                  >
                    {dayLabel(data[i].label)}
                  </text>
                ),
            )}
            {positions[index] && mode === 'cumulative' ? (
              <g>
                <line
                  x1={positions[index].x}
                  x2={positions[index].x}
                  y1={top}
                  y2={bottom}
                  stroke={vars.color.textTertiary}
                  opacity={0.5}
                />
                <circle
                  cx={positions[index].x}
                  cy={positions[index].y}
                  r={5}
                  fill={vars.color.surface}
                  stroke={vars.color.accent}
                  strokeWidth={2.5}
                />
              </g>
            ) : null}
          </svg>
        </div>
      )}
      <div className={styles.footer}>
        <div className={styles.copy}>
          <span className={styles.caption}>{chartLabel}</span>
          <span className={styles.caption}>
            Avulsas, primeiras cobranças e renovações · antes das taxas e perdas
          </span>
        </div>
        {active && !amount.isZero() && !live ? (
          <div className={styles.detail}>
            <Button
              variant="text"
              size="sm"
              aria-label="Ver intervalo anterior"
              disabled={index === 0}
              onClick={() => setSelected(index - 1)}
            >
              <ChevronLeft size={16} />
            </Button>
            <div className={styles.pointValue}>
              <span className={styles.caption}>
                {mode === 'cumulative' ? 'Acumulado até ' : ''}
                {dayLabel(active.through)}
              </span>
              <span>{formatMoney(active.amount.toFixed(3), currency)}</span>
            </div>
            <Button
              variant="text"
              size="sm"
              aria-label="Ver próximo intervalo"
              disabled={index === data.length - 1}
              onClick={() => setSelected(index + 1)}
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        ) : null}
      </div>
    </>
  )

  return live ? (
    <div className={styles.liveHero}>{content}</div>
  ) : (
    <Card padding="lg" className={styles.hero}>
      {content}
    </Card>
  )
}
